import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DataSource } from '@angular/cdk/table';
import { CollectionViewer } from '@angular/cdk/collections';

import { AssististService } from '../assistist.service';
import { DocumentDTO } from '../Document';

import { pageSizeOptions } from 'src/app/shared/utils/pagination';
import { LocalStorageService } from 'src/app/storage/local-storage.service';
import { SessionService } from 'src/app/session.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { UiService } from 'src/app/ui/ui.service';
import { AreYouSureDialogComponent } from 'src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component';
import { ElementLoaderDirective } from 'src/app/ui/element-loader/element-loader.directive';
import { find } from 'lodash';
import { getAssististDocumentLabels } from '../document-labels';

@Component({
  selector: 'tripist-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss']
})
export class assististDocumentsListComponent implements OnInit, AfterViewInit, OnDestroy {
  pageSizeOptions = pageSizeOptions;
  searchValueControl = new FormControl('');
  categorySearchControl = new FormControl('');
  tagSearchControl = new FormControl('');
  searchModeControl = new FormControl('OR');
  columns: any[] = [];
  displayedColumns: string[] = [];
  perPage = 20;
  featureType: string = ''; // 'tripist', 'explorist', or 'aura'

  @ViewChild('documentsTable') table!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(ElementLoaderDirective) elementLoader!: ElementLoaderDirective;

  hasWrite = false;
  hasDetail = false;
  private destroy$ = new Subject<void>();
  onFilterColumnChange = new Subject<void>();
  dataSource!: DocumentsDataSource;
  private currentIntegration: any;
  private integrationID!: number;
  private _filterOpen: boolean = false;
  hasIntegrationMockBookings = false;
  hasCheckedIntegrationMockBookings = false;

  // Getter methods for display text
  get itemsText(): string {
    return getAssististDocumentLabels(this.featureType).plural;
  }

  get createNewItemText(): string {
    return `Create New ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get itemText(): string {
    return getAssististDocumentLabels(this.featureType).singular;
  }

  get newItemText(): string {
    return `New ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get existingItemsText(): string {
    return `Existing ${getAssististDocumentLabels(this.featureType).plural}`;
  }

  get subtitleColumnLabel(): string {
    return getAssististDocumentLabels(this.featureType).subtitleLabel;
  }

  get isAura(): boolean {
    return this.featureType === 'aura';
  }

  isBookableDocument(doc: Partial<DocumentDTO> | null | undefined): boolean {
    if (!doc) {
      return false;
    }
    const hasBookingEngine = !!doc.booking_engine_integration_id;
    const hasForeignKey = !!(doc.foreign_key && String(doc.foreign_key).trim().length);
    return hasBookingEngine && hasForeignKey;
  }

  private initializeColumns(): void {
    const subtitleLabel = getAssististDocumentLabels(this.featureType).subtitleLabel;
    
    this.columns = [
      { code: 'id', name: 'ID' },
      { code: 'name', name: 'Document Name', filter: true, selected: true },
      { code: 'subtitle', name: subtitleLabel, filter: true, selected: true },
      { code: 'foreign_key', name: 'Foreign Key', filter: true, selected: true },
      { code: 'publish', name: 'Publish', filter: true, selected: true },
      { code: 'publish_start_date', name: 'Publish Start', filter: true, selected: false },
      { code: 'publish_end_date', name: 'Publish End', filter: true, selected: false },
      ...(this.isAura ? [{ code: 'bookable', name: 'Bookable', filter: false, selected: true }] : []),
      { code: 'categories', name: 'Categories', filter: true, selected: true },
      { code: 'options' },
    ];
    this.displayedColumns = this.columns.map(c => c.code);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private svc: AssististService,
    private session: SessionService,
    private notification: NotificationService,
    private ui: UiService,
    private cd: ChangeDetectorRef,
    private storage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.storage.pageSize || 20;
    
    // Subscribe to route parameter and data changes to handle navigation between tripist/explorist
    merge(
      this.activatedRoute.params,
      this.activatedRoute.data
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Update integration data when route changes
      this.updateFromRouteParams();
    });
    
    // Also handle initial load
    this.updateFromRouteParams();
  }

  private updateFromRouteParams(): void {
    const perms = this.activatedRoute.snapshot.data.permissions!;
    this.hasWrite = perms.hasDocumentsWrite;
    this.hasDetail = perms.hasDocumentsDetail;

    // Get the feature type from the route to determine provider type
    const featureType = this.activatedRoute.snapshot.params['featureType']; // 'tripist' | 'explorist' | 'aura'
    const providerType = featureType === 'tripist'
      ? 'tripist_assistant'
      : featureType === 'aura'
        ? 'aura_assistant'
        : 'explorist_assistant';

    this.currentIntegration = find(
      this.activatedRoute.snapshot.data.integrations,
      (i: any) => i.provider.type === providerType
    );
    this.integrationID = this.currentIntegration.id;
    this.hasIntegrationMockBookings = false;
    this.hasCheckedIntegrationMockBookings = false;
    
    this.featureType = featureType;
    
    // Initialize columns based on feature type
    this.initializeColumns();
    
    // Restore saved filter state
    this.restoreFilterState();
    
    // Update displayed columns based on selected columns
    this.updateDisplayedColumns();
    
    // Trigger data reload if data source is already initialized
    if (this.dataSource && this.paginator) {
      this.loadPage().pipe(first()).subscribe();
    }

    this.refreshIntegrationMockBookingsAvailability();
  }

  ngAfterViewInit(): void {
    this.dataSource = new DocumentsDataSource(this.svc, this.paginator, this.elementLoader);

    // Set default sort by name
    this.sort.sort({ id: 'name', start: 'asc', disableClear: false });

    // initial load
    this.loadPage().pipe(first()).subscribe();

    // react to sort, page, filter or search changes
    merge(
      this.sort.sortChange.pipe(tap(() => (this.paginator.pageIndex = 0))),
      this.paginator.page,
      this.onFilterColumnChange,
      this.searchValueControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.saveFilterState();
        })
      ),
      this.categorySearchControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.saveFilterState();
        })
      ),
      this.tagSearchControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.saveFilterState();
        })
      ),
      this.searchModeControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.saveFilterState();
        })
      ),
    )
      .pipe(
        debounceTime(300),
        switchMap(() => this.loadPage().pipe(first())),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get currentAccountID(): number {
    return this.session.currentAccountValue!.accountID;
  }

  get filterOpen() {
    return this._filterOpen;
  }

  set filterOpen(value: boolean) {
    this._filterOpen = value;
  }

  loadPage() {
    const sortParam = this.sort.direction
      ? [`${this.sort.active}:${this.sort.direction}`]
      : [];
    
    const params: any = {
      page: this.paginator.pageIndex,
      perPage: this.paginator.pageSize,
      keyword: this.searchValueControl.value ?? '',
      forCount: true,
      sort: sortParam,
      filter_columns: this.columns
        .filter(c => c.selected && c.code !== 'bookable')
        .map(c => c.code),
    };

    // Add category and tag search parameters if they have values
    if (this.categorySearchControl.value) {
      params.category_search = this.categorySearchControl.value;
    }
    if (this.tagSearchControl.value) {
      params.tag_search = this.tagSearchControl.value;
    }
    if (this.searchModeControl.value) {
      params.search_mode = this.searchModeControl.value;
    }

    return this.dataSource.loadDocuments(this.currentAccountID, this.integrationID, params);
  }

  create() {
    this.router.navigate(['create'], { relativeTo: this.activatedRoute.parent });
  }

  preview() {
    this.router.navigate(['preview'], { relativeTo: this.activatedRoute.parent });
  }

  edit(doc: DocumentDTO) {
    this.router.navigate([doc.id], { relativeTo: this.activatedRoute.parent });
  }

  delete(doc: DocumentDTO) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Delete ${this.itemText.toLowerCase()} "${doc.name}"?`,
            warning: 'This cannot be undone!',
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(yes => {
        if (yes) {
          this.ui
            .waitForEvent(this.svc.deleteDocument(this.currentAccountID, this.integrationID, doc.id))
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              () => {
                this.notification.openSuccessSnackBar(`${this.itemText} deleted`);
                this.loadPage().pipe(first()).subscribe();
              },
              () => this.notification.openErrorSnackBar(`Error deleting ${this.itemText.toLowerCase()}`),
            );
        }
      });
  }

  clearIntegrationMockBookings(): void {
    if (!this.hasWrite || !this.hasIntegrationMockBookings) {
      return;
    }

    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: 'Clear all mock booking data for this integration?',
            warning: 'This removes all currently booked mock records across all documents in this integration.',
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(yes => {
        if (!yes) {
          return;
        }

        this.ui
          .waitForEvent(this.svc.listDocuments(this.currentAccountID, this.integrationID, {
            page: 0,
            perPage: 10000,
            keyword: '',
            forCount: false,
            sort: ['id:asc'],
            filter_columns: ['id', 'booking_engine_integration_id'],
          }))
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response: any) => {
              const docs = Array.isArray(response?.records) ? response.records : [];
              const docsWithBookingEngine = docs.filter((doc: any) => !!doc?.booking_engine_integration_id);

              if (!docsWithBookingEngine.length) {
                this.hasIntegrationMockBookings = false;
                this.hasCheckedIntegrationMockBookings = true;
                this.notification.openSuccessSnackBar('No mock booking records found');
                return;
              }

              const clearRequests = docsWithBookingEngine.map((doc: any) =>
                this.svc.clearDocumentMockBookings(this.currentAccountID, this.integrationID, doc.id).pipe(
                  catchError(() => of({ deleted_count: 0 })),
                ),
              );

              this.ui
                .waitForEvent(forkJoin(clearRequests))
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                  () => {
                    this.notification.openSuccessSnackBar('Mock booking data cleared for integration');
                    this.refreshIntegrationMockBookingsAvailability();
                    this.loadPage().pipe(first()).subscribe();
                  },
                  () => this.notification.openErrorSnackBar('Error clearing integration mock booking data'),
                );
            },
            () => this.notification.openErrorSnackBar('Error loading documents for mock booking cleanup'),
          );
      });
  }

  onColumnSelectionChange(event: MatCheckboxChange, column: any) {
    column.selected = !column.selected;
    this.updateDisplayedColumns();
    this.saveFilterState();
    this.onFilterColumnChange.next();
  }

  get filterableColumns() {
    return this.columns.filter(c => c.filter); // Only return filterable columns for the menu
  }

  get hasActiveFilters(): boolean {
    // Check if there are any active filters
    const hasSearchValue = !!(this.searchValueControl.value && this.searchValueControl.value.trim());
    const hasCategorySearch = !!(this.categorySearchControl.value && this.categorySearchControl.value.trim());
    const hasTagSearch = !!(this.tagSearchControl.value && this.tagSearchControl.value.trim());
    const hasNonDefaultSearchMode = this.searchModeControl.value !== 'OR';
    
    // Check if any columns are deselected from their default state
    const hasModifiedColumns = this.columns.some(column => {
      // Default selected columns based on initial configuration
      // The options column is always displayed, so it shouldn't be considered for default state comparison
      if (column.code === 'options') return false;
      const defaultSelected = column.code !== 'publish_start_date' && column.code !== 'publish_end_date' && column.code !== 'id';
      return column.selected !== defaultSelected;
    });

    return hasSearchValue || hasCategorySearch || hasTagSearch || hasNonDefaultSearchMode || hasModifiedColumns;
  }

  private updateDisplayedColumns() {
    this.displayedColumns = this.columns
      .filter(c => c.selected !== false || c.code === 'options') // Always include options column
      .map(c => c.code);
  }

  onPageChange(e: PageEvent) {
    this.storage.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

  getSortedCategoryNames(documentCategoryKeywords: any[]): string {
    if (!documentCategoryKeywords || documentCategoryKeywords.length === 0) {
      return '';
    }
    
    return documentCategoryKeywords
      .map(catKeyword => catKeyword.category.name)
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  }

  clearAllFilters() {
    this.searchValueControl.setValue('');
    this.categorySearchControl.setValue('');
    this.tagSearchControl.setValue('');
    this.searchModeControl.setValue('OR');
    this.saveFilterState();
  }

  resetAllFilters() {
    // Reset all filters and columns to their default state
    this.searchValueControl.setValue('');
    this.categorySearchControl.setValue('');
    this.tagSearchControl.setValue('');
    this.searchModeControl.setValue('OR');
    this._filterOpen = false;
    
    // Reset columns to their default state
    this.columns.forEach(column => {
      // The options column should always be visible, so don't reset it
      if (column.code === 'options') return;
      const defaultSelected = column.code !== 'publish_start_date' && column.code !== 'publish_end_date' && column.code !== 'id';
      column.selected = defaultSelected;
    });
    
    this.updateDisplayedColumns();
    this.saveFilterState();
  }

  toggleFilterDialog() {
    this.filterOpen = !this.filterOpen;
    this.saveFilterState();
  }

  resetFilters() {
    this.categorySearchControl.setValue('');
    this.tagSearchControl.setValue('');
    this.searchModeControl.setValue('OR');
    this.saveFilterState();
  }

  applyFilters() {
    this.filterOpen = false;
    this.saveFilterState();
    // The loadPage will be triggered automatically by the valueChanges observers
  }

  private restoreFilterState() {
    const savedFilters = this.storage.getObject(
      'documentsListFilters',
      this.currentAccountID,
      this.integrationID
    );

    if (savedFilters) {
      this.searchValueControl.setValue(savedFilters.searchValue || '');
      this.categorySearchControl.setValue(savedFilters.categorySearch || '');
      this.tagSearchControl.setValue(savedFilters.tagSearch || '');
      this.searchModeControl.setValue(savedFilters.searchMode || 'OR');
      this._filterOpen = savedFilters.filterOpen || false;
      
      // Restore column selections
      if (savedFilters.columns) {
        this.columns.forEach(column => {
          const savedColumn = savedFilters.columns.find((c: any) => c.code === column.code);
          if (savedColumn && savedColumn.selected !== undefined) {
            column.selected = savedColumn.selected;
          }
        });
      }
    }
  }

  private saveFilterState() {
    const filterState = {
      searchValue: this.searchValueControl.value,
      categorySearch: this.categorySearchControl.value,
      tagSearch: this.tagSearchControl.value,
      searchMode: this.searchModeControl.value,
      filterOpen: this._filterOpen,
      columns: this.columns.map(column => ({
        code: column.code,
        selected: column.selected
      }))
    };

    this.storage.setObject(
      'documentsListFilters',
      this.currentAccountID,
      this.integrationID,
      filterState
    );
  }

  private refreshIntegrationMockBookingsAvailability(): void {
    this.ui
      .waitForEvent(this.svc.listDocuments(this.currentAccountID, this.integrationID, {
        page: 0,
        perPage: 10000,
        keyword: '',
        forCount: false,
        sort: ['id:asc'],
        filter_columns: ['id', 'booking_engine_integration_id'],
      }))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          const docs = Array.isArray(response?.records) ? response.records : [];
          const docsWithBookingEngine = docs.filter((doc: any) => !!doc?.booking_engine_integration_id);
          if (!docsWithBookingEngine.length) {
            this.hasIntegrationMockBookings = false;
            this.hasCheckedIntegrationMockBookings = true;
            return;
          }

          const detailRequests = docsWithBookingEngine.map((doc: any) =>
            this.svc.getDocument(this.currentAccountID, this.integrationID, doc.id).pipe(
              catchError(() => of(null)),
            ),
          );

          this.ui
            .waitForEvent(forkJoin(detailRequests))
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (documents: any[]) => {
                this.hasIntegrationMockBookings = documents.some((document: any) => Number(document?.mock_bookings_count || 0) > 0);
                this.hasCheckedIntegrationMockBookings = true;
              },
              () => {
                this.hasIntegrationMockBookings = false;
                this.hasCheckedIntegrationMockBookings = true;
              },
            );
        },
        () => {
          this.hasIntegrationMockBookings = false;
          this.hasCheckedIntegrationMockBookings = true;
        },
      );
  }
}

/** DataSource for documents */
export class DocumentsDataSource implements DataSource<DocumentDTO> {
  private subject = new BehaviorSubject<DocumentDTO[]>([]);

  constructor(
    private svc: AssististService,
    private paginator: MatPaginator,
    private loader: ElementLoaderDirective,
  ) {}

  connect(_collectionViewer: CollectionViewer): Observable<DocumentDTO[]> {
    return this.subject.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  loadDocuments(masterAccountID: number, integrationID: number, params: any) {
    return this.loader
      .waitForEvent(
        this.svc.listDocuments(masterAccountID, integrationID, params).pipe(
          catchError(() => of({ count: 0, records: [] })),
        ),
      )
      .pipe(
        tap((resp: any) => {
          this.paginator.length = resp.count;
          this.subject.next(resp.records);
        }),
      );
  }
}
